import React, { useEffect, useState, useRef, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import NavbarComponent from '../components/NavbarComponent';
import MobileBottomNav from '../components/MobileBottomNav';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../Auth/AuthContext';
import '../styles/forfeeder-theme.css';
import '../styles/ProductDetailsPage.css';

const ProductDetailsPage = () => {
  const { group, subgroup, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedVariantPrice, setSelectedVariantPrice] = useState(null);
  const [selectedVariantAvailable, setSelectedVariantAvailable] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [itemInCart, setItemInCart] = useState(null); // Track if item is in cart
  const [cartQuantity, setCartQuantity] = useState(0); // Current cart quantity for this item
  
  const { addToCart, cartItems, updateCartQuantity, removeFromCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const prodRef = doc(db, 'store', 'dhanaura', 'groups', group, 'subgroups', subgroup, 'products', productId);
        const prodSnap = await getDoc(prodRef);
        if (prodSnap.exists()) {
          const productData = prodSnap.data();
          setProduct(productData);
          
          // Handle variant selection
          if (productData.variants && Object.keys(productData.variants).length > 0) {
            const variants = Object.entries(productData.variants);
            const sortedVariants = variants.sort(([, dataA], [, dataB]) => {
              const availableA = parseInt(dataA.available || 0);
              const availableB = parseInt(dataB.available || 0);
              return availableB - availableA;
            });
            
            const [firstVariant, firstVariantData] = sortedVariants[0] || [];
            
            if (firstVariant) {
              setSelectedVariant(firstVariant);
              setSelectedVariantPrice(firstVariantData.price);
              setSelectedVariantAvailable(parseInt(firstVariantData.available || 0));
            }
          } else {
            setSelectedVariantAvailable(parseInt(productData.available || 0));
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [group, subgroup, productId]);

  // Fetch related products
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        const q = query(
          collection(db, 'store', 'dhanaura', 'groups', group, 'subgroups', subgroup, 'products'),
          orderBy('createdAt', 'desc'),
          limit(6)
        );
        
        const snap = await getDocs(q);
        const products = snap.docs
          .filter(doc => doc.id !== productId)
          .map(doc => ({ ...doc.data(), id: doc.id }))
          .slice(0, 4);
          
        setRelatedProducts(products);
      } catch (error) {
        console.error("Error fetching related products:", error);
      }
    };
    
    if (product) {
      fetchRelatedProducts();
    }
  }, [group, subgroup, productId, product]);

  // Check if current product/variant is in cart
  useEffect(() => {
    if (!cartItems || !product || !productId) {
      setItemInCart(null);
      setCartQuantity(0);
      return;
    }

    // Create itemKey to match cart structure
    const variantKey = selectedVariant || product.weight || 'default';
    const itemKey = `${productId}_${variantKey}`;
    
    const cartItem = cartItems.find(item => item.itemKey === itemKey);

    setItemInCart(cartItem);
    setCartQuantity(cartItem ? parseInt(cartItem.qty) : 0);
  }, [cartItems, productId, selectedVariant, product]);

  // If no variant is selected but product has variants, select the first available one
  useEffect(() => {
    if (product && product.variants && Object.keys(product.variants).length > 0 && !selectedVariant) {
      const variants = Object.entries(product.variants);
      const availableVariants = variants.filter(([, data]) => parseInt(data.available || 0) > 0);
      
      if (availableVariants.length > 0) {
        const [firstVariant, firstVariantData] = availableVariants[0];
        setSelectedVariant(firstVariant);
        setSelectedVariantPrice(firstVariantData.price);
        setSelectedVariantAvailable(parseInt(firstVariantData.available || 0));
      }
    }
  }, [product, selectedVariant]);

  const handleVariantSelect = (variant, price, available) => {
    setSelectedVariant(variant);
    setSelectedVariantPrice(price);
    
    if (product?.loose_item) {
      const variantWeight = parseFloat(variant.match(/\d+\.?\d*/)?.[0] || 0);
      const variantData = product.variants?.[variant] || {};
      const variantUnit = variantData.unit || (variant.toLowerCase().includes('kg') ? 'kg' : 'gram');
      const productUnit = product.availableUnit || 'kg';
      
      let variantWeightInProductUnit = variantWeight;
      if (variantUnit !== productUnit) {
        if (variantUnit === 'gram' && productUnit === 'kg') {
          variantWeightInProductUnit = variantWeight / 1000;
        } else if (variantUnit === 'kg' && productUnit === 'gram') {
          variantWeightInProductUnit = variantWeight * 1000;
        }
      }
      
      const availableProduct = parseFloat(product.available || 0);
      const availableUnits = Math.floor(availableProduct / variantWeightInProductUnit);
      setSelectedVariantAvailable(availableUnits);
    } else {
      setSelectedVariantAvailable(parseInt(available || 0));
    }
    
    setQuantity(1);
  };
  
  const handleQuantityChange = (delta) => {
    const maxQuantity = getCurrentAvailable();
    const newQuantity = Math.max(1, Math.min(maxQuantity, quantity + delta));
    
    if (delta > 0 && quantity >= maxQuantity) {
      alert(`Sorry, only ${maxQuantity} items available.`);
      return;
    }
    
    setQuantity(newQuantity);
  };
  
  const handleAddToCart = async () => {
    if (!user) {
      alert("Please log in to add items to your cart");
      navigate('/login');
      return;
    }
    
    // Auto-select first available variant if none is selected but variants exist
    let variantToUse = selectedVariant;
    if (product?.variants && Object.keys(product.variants).length > 0 && !selectedVariant) {
      const availableVariants = Object.entries(product.variants)
        .filter(([_, data]) => parseInt(data.available || 0) > 0);
      
      if (availableVariants.length > 0) {
        const [firstVariant, firstVariantData] = availableVariants[0];
        variantToUse = firstVariant;
        // Auto-select this variant
        handleVariantSelect(firstVariant, firstVariantData.price, firstVariantData.available);
      } else {
        alert("Product is out of stock");
        return;
      }
    }
    
    if (!variantToUse && product?.variants && Object.keys(product.variants).length > 0) {
      variantToUse = product.weight || 'default';
    } else if (!variantToUse) {
      variantToUse = product.weight || 'default';
    }
    
    setAddingToCart(true);
    
    try {
      const success = await addToCart(
        {
          ...product,
          id: productId,
          group,
          subgroup
        },
        variantToUse,
        1 // Always add 1 initially for the bottom nav pattern
      );
      
      if (success) {
        // Don't show alert, the UI will update automatically
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      alert('Error adding to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleCartQuantityChange = async (delta) => {
    if (!itemInCart) return;
    
    const newQuantity = cartQuantity + delta;
    const maxQuantity = getCurrentAvailable();
    
    if (newQuantity <= 0) {
      // Remove from cart
      try {
        await removeFromCart(itemInCart.itemKey);
      } catch (err) {
        console.error('Error removing from cart:', err);
      }
      return;
    }
    
    if (newQuantity > maxQuantity) {
      alert(`Sorry, only ${maxQuantity} items available.`);
      return;
    }
    
    try {
      await updateCartQuantity(itemInCart.itemKey, delta);
    } catch (err) {
      console.error('Error updating cart quantity:', err);
    }
  };
  
  const isOutOfStock = () => {
    if (!product) return true;
    
    if (product.loose_item) {
      if (!selectedVariant) {
        return parseFloat(product.available || 0) <= 0;
      }
      return getCurrentAvailable() <= 0;
    }
    
    if (product.variants && Object.keys(product.variants).length > 0) {
      if (!selectedVariant) {
        // If no variant is selected, check if any variant is available
        return !Object.entries(product.variants).some(([_, data]) => parseInt(data.available || 0) > 0);
      }
      return selectedVariantAvailable <= 0;
    }
    
    return parseInt(product.available || 0) <= 0;
  };
  
  const getCurrentAvailable = () => {
    if (!product) return 0;
    
    if (product.loose_item && selectedVariant) {
      const variantWeight = parseFloat(selectedVariant.match(/\d+\.?\d*/)?.[0] || 0);
      const variantData = product.variants?.[selectedVariant] || {};
      const variantUnit = variantData.unit || (selectedVariant.toLowerCase().includes('kg') ? 'kg' : 'gram');
      const productUnit = product.availableUnit || 'kg';
      
      let variantWeightInProductUnit = variantWeight;
      if (variantUnit !== productUnit) {
        if (variantUnit === 'gram' && productUnit === 'kg') {
          variantWeightInProductUnit = variantWeight / 1000;
        } else if (variantUnit === 'kg' && productUnit === 'gram') {
          variantWeightInProductUnit = variantWeight * 1000;
        }
      }
      
      const available = parseFloat(product.available || 0);
      return Math.floor(available / variantWeightInProductUnit);
    }
    
    if (product.variants && Object.keys(product.variants).length > 0 && !product.loose_item) {
      if (!selectedVariant) return 0;
      return selectedVariantAvailable;
    }
    
    return parseInt(product.available || 0);
  };

  const getVariantDisplayText = (variant) => {
    if (!product?.variants || !variant) return '';
    
    const variantData = product.variants[variant];
    if (!variantData) return variant;
    
    // Extract weight and unit from variant key or data
    const weight = parseFloat(variant.match(/\d+\.?\d*/)?.[0] || 0);
    const unit = variantData.unit || (variant.toLowerCase().includes('kg') ? 'kg' : 'gram');
    
    return `${weight} ${unit}`;
  };

  const getDiscountPercentage = () => {
    if (!product) return 0;
    
    const currentPrice = selectedVariantPrice || product.wSale || product.price;
    let originalPrice = product.originalPrice || product.mrp;
    
    // Check if selected variant has its own MRP
    if (selectedVariant && product.variants && product.variants[selectedVariant]) {
      const variantData = product.variants[selectedVariant];
      originalPrice = variantData.mrp || variantData.originalPrice || originalPrice;
    }
    
    if (originalPrice && currentPrice && originalPrice > currentPrice) {
      return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
    }
    return 0;
  };

  if (loading) {
    return (
      <>
        <NavbarComponent />
        <div >
          <div className="product-detail-skeleton">
            <div className="skeleton-image"></div>
            <div className="skeleton-content">
              <div className="skeleton-title"></div>
              <div className="skeleton-price"></div>
              <div className="skeleton-description"></div>
              <div className="skeleton-button"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <NavbarComponent />
        <div >
          <div className="error-container">
            <div className="error-icon">📦</div>
            <h3>Product Not Found</h3>
            <p>We couldn't find the product you're looking for.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  const images = product?.imageData || [];
  const currentPrice = selectedVariantPrice || product.wSale || product.price || 0;
  
  // Get original price - check variant first, then product
  let originalPrice = product.originalPrice || product.mrp;
  if (selectedVariant && product.variants && product.variants[selectedVariant]) {
    const variantData = product.variants[selectedVariant];
    originalPrice = variantData.mrp || variantData.originalPrice || originalPrice;
  }
  
  const discountPercentage = getDiscountPercentage();

  return (
    <>
      <NavbarComponent />
      
      {/* Mobile Header */}
      <div className="mobile-product-header d-block d-md-none">
        <div className="d-flex align-items-center p-3">
          <button 
            className="btn btn-link p-0 me-3"
            onClick={() => navigate(-1)}
          >
            <i className="bi bi-arrow-left fs-4"></i>
          </button>
          <h6 className="mb-0 text-truncate flex-grow-1">{product.name}</h6>
          <button className="btn btn-link p-0">
            <i className="bi bi-share fs-5"></i>
          </button>
        </div>
      </div>

      <div >
        {/* Desktop Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-3 d-none d-md-block">
          <div className="d-flex align-items-center text-sm text-muted">
            <Link to="/" className="text-decoration-none text-primary">
              <i className="bi bi-house me-1"></i>Home
            </Link>
            <i className="bi bi-chevron-right mx-2 text-muted"></i>
            <Link to={`/category/${group}`} className="text-decoration-none text-primary text-capitalize">
              {group.replace(/_/g, ' ')}
            </Link>
            <i className="bi bi-chevron-right mx-2 text-muted"></i>
            <span className="text-dark fw-medium">{product.name}</span>
          </div>
        </nav>

        <div className="product-detail-container">
          {/* Product Images Section - Full Width on Mobile */}
          <div className="product-image-section">
            {images.length > 0 ? (
              <>
                <div className="main-image-container">
                  {/* Image Navigation Arrows */}
                  {images.length > 1 && (
                    <>
                      <button 
                        className="image-nav-btn prev-btn"
                        onClick={() => setSelectedImageIdx(selectedImageIdx > 0 ? selectedImageIdx - 1 : images.length - 1)}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                      <button 
                        className="image-nav-btn next-btn"
                        onClick={() => setSelectedImageIdx(selectedImageIdx < images.length - 1 ? selectedImageIdx + 1 : 0)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </>
                  )}
                  
                  <img
                    src={images[selectedImageIdx]}
                    alt={product.name}
                    className="main-product-image"
                  />
                  
                  {discountPercentage > 0 && (
                    <div className="discount-badge-large">
                      {discountPercentage}% OFF
                    </div>
                  )}
                  
                  {/* Image navigation dots */}
                  {images.length > 1 && (
                    <div className="image-dots">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          className={`dot ${selectedImageIdx === idx ? 'active' : ''}`}
                          onClick={() => setSelectedImageIdx(idx)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="no-image-placeholder">
                <i className="bi bi-image fs-1 text-muted"></i>
                <p className="text-muted">No image available</p>
              </div>
            )}
          </div>

          {/* Product Info Section */}
          <div className="product-info-section">
            {/* Product Name */}
            <h1 className="product-title">{product.name}</h1>
            
            {/* Net Qty Display */}
            {(selectedVariant || (!product.variants || Object.keys(product.variants).length === 0)) && (
              <div className="net-qty">
                Net Qty: {selectedVariant ? getVariantDisplayText(selectedVariant) : (product.weight || '1 unit')}
              </div>
            )}
            
            {/* Compact Price/Discount/Variant Row */}
            <div className="compact-price-row">
              <div className="price-discount-section">
                <span className="current-price">₹{currentPrice}</span>
                {discountPercentage > 0 && (
                  <span className="discount-percent">{discountPercentage}% off</span>
                )}
              </div>
              
              {/* Variant Selection Dropdown */}
              {product.variants && Object.keys(product.variants).length > 0 && (
                <div className="variant-dropdown-compact">
                  <select 
                    className="variant-dropdown"
                    value={selectedVariant || ''}
                    onChange={(e) => {
                      const variant = e.target.value;
                      const data = product.variants[variant];
                      if (data) {
                        handleVariantSelect(variant, data.price, data.available);
                      }
                    }}
                  >
                    <option value="" disabled>Select Size</option>
                    {Object.entries(product.variants).map(([variant, data]) => {
                      const isVariantOutOfStock = parseInt(product.available || 0) <= 0;
                      return (
                        <option 
                          key={variant} 
                          value={variant}
                          // Don't disable, let user select to see it's out of stock
                        >
                          {getVariantDisplayText(variant)} {isVariantOutOfStock ? '(Out of Stock)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>
            
            {/* Original Price with Cross */}
            {originalPrice && originalPrice > currentPrice && (
              <div className="original-price-row">
                <span className="original-price">₹{originalPrice}</span>
              </div>
            )}

            {/* Stock Status */}
            {isOutOfStock() ? (
              <div className="stock-status out-of-stock">
                <i className="bi bi-x-circle"></i>
                <span>Out of Stock</span>
              </div>
            ) : (
              <div className="stock-status in-stock">
                <i className="bi bi-check-circle"></i>
                <span>{getCurrentAvailable()} left</span>
              </div>
            )}

            {/* Inline Add to Cart Section - Always Visible */}
            <div className="add-to-cart-section-inline">
              {isOutOfStock() ? (
                <button className="add-to-cart-btn-inline out-of-stock" disabled>
                  <i className="bi bi-x-circle me-2"></i>
                  Out of Stock
                </button>
              ) : itemInCart && cartQuantity > 0 ? (
                <div className="quantity-controls-inline">
                  <button 
                    className="quantity-btn minus"
                    onClick={() => handleCartQuantityChange(-1)}
                  >
                    <i className="bi bi-dash"></i>
                  </button>
                  <span className="quantity-display">{cartQuantity}</span>
                  <button 
                    className="quantity-btn plus"
                    onClick={() => handleCartQuantityChange(1)}
                    disabled={cartQuantity >= getCurrentAvailable()}
                  >
                    <i className="bi bi-plus"></i>
                  </button>
                </div>
              ) : (
                <button 
                  className="add-to-cart-btn-inline"
                  onClick={handleAddToCart}
                  disabled={addingToCart || isOutOfStock()}
                >
                  {addingToCart ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Adding to Cart...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cart-plus me-2"></i>
                      Add to Cart
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Product Description */}
            {product.about && (
              <div className="product-description">
                <h6 className="description-title">About this product</h6>
                <div className={`description-content ${showFullDescription ? 'expanded' : ''}`}>
                  <p>{product.about}</p>
                </div>
                {product.about.length > 150 && (
                  <button
                    className="description-toggle"
                    onClick={() => setShowFullDescription(!showFullDescription)}
                  >
                    {showFullDescription ? 'Show Less' : 'Read More'}
                  </button>
                )}
              </div>
            )}

            {/* Add to Cart Section - Desktop/All Views */}
            {/* <div className="add-to-cart-section-desktop">
              {isOutOfStock() ? (
                <button className="add-to-cart-btn-desktop out-of-stock" disabled>
                  <i className="bi bi-x-circle me-2"></i>
                  Out of Stock
                </button>
              ) : itemInCart && cartQuantity > 0 ? (
                <div className="quantity-controls-desktop">
                  <button 
                    className="quantity-btn minus"
                    onClick={() => handleCartQuantityChange(-1)}
                  >
                    <i className="bi bi-dash"></i>
                  </button>
                  <span className="quantity-display">{cartQuantity}</span>
                  <button 
                    className="quantity-btn plus"
                    onClick={() => handleCartQuantityChange(1)}
                    disabled={cartQuantity >= getCurrentAvailable()}
                  >
                    <i className="bi bi-plus"></i>
                  </button>
                </div>
              ) : (
                <button 
                  className="add-to-cart-btn-desktop"
                  onClick={handleAddToCart}
                  disabled={addingToCart || isOutOfStock()}
                >
                  {addingToCart ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Adding to Cart...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cart-plus me-2"></i>
                      Add to Cart
                    </>
                  )}
                </button>
              )}
            </div> */}
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="related-products-section">
              <h5 className="section-title">You might also like</h5>
              <div className="related-products-grid">
                {relatedProducts.map((relatedProduct) => {
                  // For products with variants, randomly select a variant for display
                  let relatedCurrentPrice = 0;
                  let relatedOriginalPrice = 0;
                  let selectedVariantText = '';
                  
                  if (relatedProduct.variants && Object.keys(relatedProduct.variants).length > 0) {
                    // Randomly select a variant to display
                    const variantKeys = Object.keys(relatedProduct.variants);
                    const randomVariantKey = variantKeys[Math.floor(Math.random() * variantKeys.length)];
                    const selectedVariant = relatedProduct.variants[randomVariantKey];
                    
                    relatedCurrentPrice = parseFloat(selectedVariant.price || 0);
                    relatedOriginalPrice = parseFloat(selectedVariant.mrp || 0);
                    
                    // Create display text for the variant (e.g., "1 kg", "500 gram")
                    const weight = parseFloat(randomVariantKey.match(/\d+\.?\d*/)?.[0] || randomVariantKey);
                    const unit = selectedVariant.unit || (randomVariantKey.toLowerCase().includes('kg') ? 'kg' : 'gram');
                    selectedVariantText = `${weight} ${unit}`;
                  } else {
                    // Fallback to product-level pricing
                    relatedCurrentPrice = parseFloat(relatedProduct.wSale || relatedProduct.price || 0);
                    relatedOriginalPrice = parseFloat(relatedProduct.originalPrice || relatedProduct.mrp || 0);
                    selectedVariantText = relatedProduct.weight || '';
                  }
                  
                  // Calculate discount percentage
                  const relatedDiscountPercentage = relatedOriginalPrice && relatedCurrentPrice && relatedOriginalPrice > relatedCurrentPrice
                    ? Math.round(((relatedOriginalPrice - relatedCurrentPrice) / relatedOriginalPrice) * 100)
                    : 0;
                  
                  return (
                    <div 
                      key={relatedProduct.id} 
                      className="related-product-card"
                      onClick={() => navigate(`/product/${group}/${subgroup}/${relatedProduct.id}`)}
                    >
                      <div className="related-product-image">
                        <img
                          src={relatedProduct.imageData?.[0] || '/placeholder-image.jpg'}
                          alt={relatedProduct.name}
                          loading="lazy"
                        />
                        {relatedDiscountPercentage > 0 && (
                          <div className="related-product-discount-badge">
                            {relatedDiscountPercentage}% OFF
                          </div>
                        )}
                      </div>
                      <div className="related-product-info">
                        <h6 className="related-product-name">{relatedProduct.name}</h6>
                        <div className="related-product-pricing">
                          <div className="related-price-row">
                            <span className="related-current-price">₹{relatedCurrentPrice}</span>
                            {relatedOriginalPrice && relatedOriginalPrice > relatedCurrentPrice && (
                              <span className="related-original-price">₹{relatedOriginalPrice}</span>
                            )}
                          </div>
                          {relatedDiscountPercentage > 0 && (
                            <div className="related-discount-text">
                              {relatedDiscountPercentage}% off
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Custom Bottom Navigation for Mobile */}
      <div className="product-bottom-nav d-block d-md-none">
        <div className="bottom-nav-content">
          {/* Cart Icon */}
          <Link to="/cart" className="cart-icon-link">
            <div className="cart-icon-container">
              <i className="bi bi-bag"></i>
              {cartItems && cartItems.length > 0 && (
                <span className="cart-count">
                  {cartItems.reduce((total, item) => total + parseInt(item.qty), 0)}
                </span>
              )}
            </div>
          </Link>
          
          {/* Add to Cart / Quantity Controls */}
          <div className="cart-action-section">
            {isOutOfStock() ? (
              <button className="add-to-cart-btn out-of-stock" disabled>
                Out of Stock
              </button>
            ) : itemInCart && cartQuantity > 0 ? (
              <div className="quantity-controls">
                <button 
                  className="quantity-btn minus"
                  onClick={() => handleCartQuantityChange(-1)}
                >
                  <i className="bi bi-dash"></i>
                </button>
                <span className="quantity-display">{cartQuantity}</span>
                <button 
                  className="quantity-btn plus"
                  onClick={() => handleCartQuantityChange(1)}
                  disabled={cartQuantity >= getCurrentAvailable()}
                >
                  <i className="bi bi-plus"></i>
                </button>
              </div>
            ) : (
              <button 
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={addingToCart || isOutOfStock()}
              >
                {addingToCart ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Adding...
                  </>
                ) : (
                  'ADD TO CART'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

    </>
  );
};

export default ProductDetailsPage;
